import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCourse } from "./hooks/useCourse";
import { showToast } from "../../../core/utils/alert";

interface CourseModalsProps {
  courseId?: string;
  onSuccess?: () => void;
}

const CourseModals: React.FC<CourseModalsProps> = ({ courseId, onSuccess }) => {
  const { deleteCourse, updateCourse } = useCourse();
  const [isPublishing, setIsPublishing] = useState(false);

  const handleDelete = async () => {
    if (!courseId) return;

    try {
      const response = await deleteCourse(courseId);
      console.log(response);
      showToast({
        title: "Success",
        text: "Course deleted successfully",
        icon: "success",
      });
      if (onSuccess) onSuccess();
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Could not delete the course",
        icon: "error",
      });
      console.error("Failed to delete course:", error);
    }
  };

  const handlePublish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId) return;

    setIsPublishing(true);
    try {
      const result = await updateCourse(courseId, { isPublished: true });
      if (result.success) {
        showToast({
          title: "Success",
          text: "Course published successfully",
          icon: "success",
        });
        if (onSuccess) onSuccess();
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not publish the course",
          icon: "error",
        });
      }
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Could not publish the course",
        icon: "error",
      });
      console.error("Failed to publish course:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash" />
              </span>
              <h4>Delete Course</h4>
              <p>
                Are you sure you want to delete this course? This action cannot
                be undone.
              </p>
              <div className="d-flex justify-content-center">
                <Link
                  to="#"
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={handleDelete}
                >
                  Delete
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Delete Modal */}

      {/* Publish Modal */}
      <div className="modal fade" id="publish-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={handlePublish}>
              <div className="modal-body text-center">
                <span className="publish-icon">
                  <i className="ti ti-send" />
                </span>
                <h4>Publish Course</h4>
                <p>
                  Are you sure you want to publish this course? Once published,
                  you can't edit the course and undo this action.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-success"
                    data-bs-dismiss="modal"
                    disabled={isPublishing}
                  >
                    {isPublishing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        ></span>
                        Publishing...
                      </>
                    ) : (
                      "Yes, Publish"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Publish Modal */}
    </>
  );
};

export default CourseModals;

export {};
